/* eslint-disable import/no-unused-modules -- Internal types used in exported generic signatures are flagged as unused. */
import { getHdeEventCalendarIcsUrl } from '~/modules/hde/api/get-hde-event-calendar-ics-url';
import { getHdeEventGoogleCalendarUrl } from '~/modules/hde/api/get-hde-event-google-calendar-url';
import { getHdeEventOutlookCalendarUrl } from '~/modules/hde/api/get-hde-event-outlook-calendar-url';
import type { StepConfig } from '~/ui-rocco/multi-step-form';
import {
    getHDEProfileCompletion,
    type HDEProfessionalProfile,
} from './profile-completion';
import {
    getPrimaryEventId,
    toComparableId,
    type LocationOption,
} from './types';
import type { User } from '~/server/types';
import type {
    FilterVisibleStepsContext,
    GetInitialDataContext,
} from '../../hooks/useHDERegistrationForm';

/** Client director team member shape from Hubspot/GraphQL. */
export interface HDEClientDirector {
    ID: string | number;
    Name?: string | null;
    JobTitle?: string | null;
    Email?: string | null;
    Phone?: string | null;
    MemberImage?: {
        ID?: string | null;
        LargeAvatar?: string | null;
        TeamMemberDisplay?: string | null;
    } | null;
}

/** Hubspot company shape including client director. */
export interface HDEProfessionalHubspotCompany {
    ClientDirectorTeamMember?: HDEClientDirector | null;
}

/** Professional profile extended with Hubspot company data. */
export interface HDEProfessionalProfileWithHubspot extends HDEProfessionalProfile {
    HubspotCompany?: HDEProfessionalHubspotCompany | null;
}

/** Base form data shared by Exhibitor and Professional registration flows. */
interface BaseProExhibitorFormData {
    isProfessionalMember: boolean | null;
    selectedProfessionalId: string | null;
    eventId: string | null;
    fullName: string;
    companyName: string;
    companyWebsite: string;
    companyEmail: string;
    phone: string;
    companyType: string | null;
    /** When companyType is "Other", user's specification (submitted directly as CompanyType). */
    companyTypeOther: string;
    companyLocation: string;
    companySelectedLocation: {
        region?: string;
        state?: string;
        city?: string;
    } | null;
    companyShowOtherInput: boolean;
}

type ApplicationType = 'Exhibitor' | 'Professional';

/**
 * Ensures website URL has https:// prefix before submit.
 * Uses the string as-is if it already starts with https:// or http:// (case-insensitive).
 * Normalises mixed-case protocols (e.g. Http://) to lowercase to avoid malformed URLs.
 */
function normalizeWebsiteUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    const protocolMatch = trimmed.match(/^(https?:\/\/)/i);
    const protocol = protocolMatch?.[1];
    if (protocol) {
        return protocol.toLowerCase() + trimmed.slice(protocol.length);
    }
    return `https://${trimmed}`;
}

/**
 * Resolve selected professional ID from form data.
 * When the user chose "No, continue with registration" (isProfessionalMember = false),
 * their explicit choice to do manual entry is respected and no profile is resolved.
 * Does not auto-select when there is only one profile; the user must explicitly
 * choose it in the business-profile-picker step.
 */
function resolveSelectedProfile(
    formData: BaseProExhibitorFormData,
    professionals: HDEProfessionalProfile[],
): HDEProfessionalProfile | null {
    if (formData.isProfessionalMember === false) {
        return null;
    }
    const resolvedSelectedId = formData.selectedProfessionalId ?? null;
    return resolvedSelectedId
        ? (professionals.find(
              (p) =>
                  toComparableId(p.ID) === toComparableId(resolvedSelectedId),
          ) ?? null)
        : null;
}

export function filterVisibleProExhibitorSteps(
    configs: StepConfig[],
    context: FilterVisibleStepsContext,
): StepConfig[] {
    const formData = context.formData as BaseProExhibitorFormData | undefined;
    const isProfessionalMember = formData?.isProfessionalMember === true;
    const hasMultipleEvents = context.locationOptionsLength > 1;
    const profileCount = context.profileCount ?? 0;
    const me = context.user as {
        Professionals?: HDEProfessionalProfile[];
        FirstName?: string;
        LastName?: string;
        Email?: string;
    } | null;
    const professionals = me?.Professionals ?? [];
    const selectedProfile = resolveSelectedProfile(
        formData ?? {
            selectedProfessionalId: null,
            isProfessionalMember: null,
            eventId: null,
            fullName: '',
            companyName: '',
            companyWebsite: '',
            companyEmail: '',
            phone: '',
            companyType: null,
            companyTypeOther: '',
            companyLocation: '',
            companySelectedLocation: null,
            companyShowOtherInput: false,
        },
        professionals,
    );
    const selectedProfileCompletion = getHDEProfileCompletion({
        profile: selectedProfile ?? null,
        memberFirstName: me?.FirstName,
        memberLastName: me?.LastName,
        memberEmail: me?.Email,
    });

    return configs.filter((step) => {
        if (step.id === 'professional-member')
            return (
                !(context.isLoggedIn && profileCount === 0) &&
                !(context.isLoggedIn && isProfessionalMember === true)
            );
        if (step.id === 'event-selection') return hasMultipleEvents;
        if (step.id === 'authentication')
            return isProfessionalMember && !context.isLoggedIn;
        if (step.id === 'business-profile-picker')
            return (
                isProfessionalMember && context.isLoggedIn && profileCount >= 1
            );
        if (step.id === 'company-name-website')
            return !selectedProfileCompletion.canSkipCompanyDetails;
        if (step.id === 'company-type')
            return (
                context.hasCompanyValidationError ||
                !context.professionalExtraction?.companyType
            );
        if (step.id === 'company-location')
            return (
                context.hasCompanyValidationError ||
                !context.professionalExtraction?.projectLocation
            );
        return true;
    });
}

/** Options for creating getInitialDataForStep. */
interface CreateGetInitialDataOptions<T extends BaseProExhibitorFormData> {
    applicationType: ApplicationType;
    socialLoginEventId: string | null;
    /** Handler for guest-invitation step (Professional only). */
    getGuestInvitationData?: (
        formData: T,
        ctx: GetInitialDataContext,
    ) => Record<string, unknown>;
    /** Extra success step props (e.g. Exhibitor adds companyName). */
    getSuccessStepExtras?: (
        formData: T,
        selectedProfile: HDEProfessionalProfile | null,
        calendarLink: string | undefined,
        googleCalendarLink: string | undefined,
    ) => Record<string, unknown>;
}

/**
 * Creates getInitialDataForStep for Exhibitor or Professional flows.
 * Consolidates shared step data logic; form-specific steps handled via options.
 */
export function createGetInitialDataForStep<T extends BaseProExhibitorFormData>(
    options: CreateGetInitialDataOptions<T>,
) {
    const {
        applicationType,
        socialLoginEventId,
        getGuestInvitationData,
        getSuccessStepExtras,
    } = options;

    return (
        stepId: string,
        formData: T,
        ctx: GetInitialDataContext,
    ): Record<string, unknown> | undefined => {
        const opts = Array.isArray(ctx.locationOptions)
            ? (ctx.locationOptions as LocationOption[])
            : [];
        /** Event ID for calendar: coming event closest to now (first in opts, sorted by nearest upcoming date). */
        const eventIdForCalendar =
            opts.length > 0
                ? (opts[0]?.value ?? null)
                : (formData.eventId ?? socialLoginEventId ?? null);
        const calendarLink = eventIdForCalendar
            ? getHdeEventCalendarIcsUrl(eventIdForCalendar)
            : undefined;
        const googleCalendarLink = eventIdForCalendar
            ? getHdeEventGoogleCalendarUrl(eventIdForCalendar)
            : undefined;
        const outlookCalendarLink = eventIdForCalendar
            ? getHdeEventOutlookCalendarUrl(eventIdForCalendar)
            : undefined;

        const me = ctx.user as {
            Professionals?: HDEProfessionalProfile[];
            FirstName?: string;
            LastName?: string;
            Email?: string;
            Phone?: string;
        } | null;
        const professionals = me?.Professionals ?? [];
        const selectedProfile = resolveSelectedProfile(formData, professionals);

        switch (stepId) {
            case 'professional-member':
                return {
                    isProfessionalMember: formData.isProfessionalMember,
                    isLoggedIn: ctx.isLoggedIn,
                    profileCount: professionals.length,
                };
            case 'event-selection':
                return {
                    eventId: formData.eventId,
                    locationOptions: ctx.locationOptions,
                };
            case 'business-profile-picker':
                return {
                    professionals,
                    selectedProfessionalId: formData.selectedProfessionalId,
                };
            case 'company-name-website':
                return {
                    fullName:
                        formData.fullName ||
                        [me?.FirstName, me?.LastName].filter(Boolean).join(' '),
                    companyEmail: formData.companyEmail || me?.Email || '',
                    companyName:
                        formData.companyName || selectedProfile?.Title || '',
                    companyWebsite:
                        formData.companyWebsite ||
                        selectedProfile?.Website ||
                        selectedProfile?.PrimaryDomain ||
                        '',
                    user: ctx.user,
                    selectedProfile,
                };
            case 'company-type':
                return {
                    companyType:
                        formData.companyType ??
                        ctx.professionalExtraction?.companyType ??
                        null,
                    companyTypeOther: formData.companyTypeOther ?? '',
                    selectedProfile,
                };
            case 'company-location':
                return {
                    variant: 'company' as const,
                    companyLocation:
                        formData.companyLocation ||
                        ctx.professionalExtraction?.projectLocation ||
                        '',
                    companySelectedLocation: formData.companySelectedLocation,
                    companyShowOtherInput: formData.companyShowOtherInput,
                };
            case 'guest-invitation':
                return getGuestInvitationData?.(formData, ctx);
            case 'authentication': {
                const eventIdToUse =
                    opts.length > 0
                        ? (opts[0]?.value ?? null)
                        : (formData.eventId ?? socialLoginEventId ?? null);
                return {
                    hdeFormData: {
                        applicationType,
                        ...formData,
                        eventId: eventIdToUse,
                        primaryEventId: getPrimaryEventId(eventIdToUse, opts),
                    },
                    showForgotPassword: ctx.showForgotPassword,
                    parentShowEmailForm: ctx.showEmailForm,
                    onBack: ctx.handleAuthEmailFormBack,
                };
            }
            case 'success': {
                const base = {
                    onDone: ctx.onClose,
                    registrationType: applicationType,
                    calendarLink,
                    googleCalendarLink,
                    outlookCalendarLink,
                    isSubmitSuccess: ctx.isSubmitSuccess,
                };
                const extras = getSuccessStepExtras?.(
                    formData,
                    selectedProfile,
                    calendarLink,
                    googleCalendarLink,
                );
                return { ...base, ...extras };
            }
            default:
                return undefined;
        }
    };
}

/** Options for creating buildSubmitPayload. */
interface CreateBuildSubmitPayloadOptions<T extends BaseProExhibitorFormData> {
    applicationType: ApplicationType;
    locationOptions: unknown;
    /** Append guest fields to submitData (Professional only). */
    appendGuestFields?: (formData: T, submitData: FormData) => void;
}

/**
 * Creates buildSubmitPayload for Exhibitor or Professional flows.
 * Consolidates shared profile resolution, name splitting, company details, and location.
 */
export function createBuildSubmitPayload<T extends BaseProExhibitorFormData>(
    options: CreateBuildSubmitPayloadOptions<T>,
) {
    const { applicationType, locationOptions, appendGuestFields } = options;

    return (
        formData: T,
        user: User | null,
        professionalExtraction?: {
            companyType: string | null;
            projectLocation: string | null;
        } | null,
    ): FormData => {
        const me =
            user && (user as { __typename?: string }).__typename === 'Me'
                ? (user as {
                      FirstName?: string;
                      LastName?: string;
                      Email?: string;
                      Phone?: string;
                      Professionals?: HDEProfessionalProfile[];
                  })
                : null;
        const submitData = new FormData();
        submitData.append('ApplicationType', applicationType);

        const optsForSubmit = Array.isArray(locationOptions)
            ? (locationOptions as LocationOption[])
            : [];
        /** Event ID: coming event closest to now (consistent with calendar/auth). */
        const eventIdToUse =
            optsForSubmit.length > 0
                ? ((optsForSubmit[0] as { value?: string })?.value ?? null)
                : (formData.eventId ?? null);
        if (eventIdToUse) {
            const eventIds = eventIdToUse.includes(',')
                ? eventIdToUse
                      .split(',')
                      .map((id) => Number(id.trim()))
                      .filter((id) => !isNaN(id))
                : [Number(eventIdToUse)];
            submitData.append('EventIDs', JSON.stringify(eventIds));
        }

        const professionals = me?.Professionals ?? [];
        const selectedProfile = resolveSelectedProfile(formData, professionals);

        if (me) {
            if (me.FirstName) submitData.append('FirstName', me.FirstName);
            if (me.LastName) submitData.append('LastName', me.LastName);
            const emailToUse = formData.companyEmail?.trim() || me.Email || '';
            if (emailToUse) submitData.append('Email', emailToUse);
            const phoneToUse = formData.phone?.trim() || me.Phone || '';
            submitData.append('Phone', phoneToUse);
        } else if (formData.fullName.trim()) {
            const nameParts = formData.fullName
                .trim()
                .split(/\s+/)
                .filter(Boolean);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ').trim() || firstName;

            if (firstName) submitData.append('FirstName', firstName);
            if (lastName) submitData.append('LastName', lastName);

            if (formData.companyEmail?.trim()) {
                submitData.append('Email', formData.companyEmail.trim());
            }
        }
        submitData.append('AutoCreateMember', 'true');
        submitData.append('IsModalSubmission', 'true');
        if (!submitData.has('Phone')) {
            submitData.append('Phone', formData.phone?.trim() || '');
        }

        if (formData.companyName)
            submitData.append('CompanyName', formData.companyName);
        else if (selectedProfile?.Title)
            submitData.append('CompanyName', selectedProfile.Title);
        if (formData.companyWebsite)
            submitData.append(
                'CompanyWebsite',
                normalizeWebsiteUrl(formData.companyWebsite),
            );
        else if (selectedProfile?.Website || selectedProfile?.PrimaryDomain)
            submitData.append(
                'CompanyWebsite',
                normalizeWebsiteUrl(
                    selectedProfile?.Website ||
                        selectedProfile?.PrimaryDomain ||
                        '',
                ),
            );
        const baseCompanyType =
            formData.companyType ?? professionalExtraction?.companyType;
        const companyTypeToSubmit =
            baseCompanyType === 'Other' && formData.companyTypeOther?.trim()
                ? formData.companyTypeOther.trim()
                : baseCompanyType;
        if (companyTypeToSubmit)
            submitData.append('CompanyType', companyTypeToSubmit);

        const selectedLocationCompanyLocation =
            formData.companySelectedLocation?.region ||
            formData.companySelectedLocation?.state ||
            formData.companySelectedLocation?.city ||
            '';
        const companyLocation =
            selectedLocationCompanyLocation ||
            formData.companyLocation.trim() ||
            professionalExtraction?.projectLocation ||
            '';
        if (companyLocation) {
            submitData.append('CompanyLocation', companyLocation);
        }
        if (formData.companySelectedLocation) {
            submitData.append(
                'HDEBuildHubLocationData',
                JSON.stringify(formData.companySelectedLocation),
            );
        }

        if (appendGuestFields) {
            appendGuestFields(formData, submitData);
        } else {
            submitData.append('BringingGuest', 'false');
        }

        return submitData;
    };
}

/**
 * Returns URLSegment of selected business profile for extraction fetch.
 * Returns null when isProfessionalMember is false so we do not trigger
 * extraction fetches for profiles the user declined to use.
 * Does not auto-select when there is only one profile; the user must explicitly
 * choose it in the business-profile-picker step.
 */
export function getSelectedProfessionalUrlSegment(
    formData: BaseProExhibitorFormData,
    user: User | null,
): string | null {
    if (formData.isProfessionalMember === false) {
        return null;
    }
    const me = user as { Professionals?: HDEProfessionalProfile[] } | null;
    const profs = me?.Professionals ?? [];
    const id = formData.selectedProfessionalId ?? null;
    return (
        profs.find((p) => toComparableId(p.ID) === toComparableId(id))
            ?.URLSegment ?? null
    );
}
